import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apartment from '../resources/apartment.webp';
import { capitalizeFirstLetters } from '../util/AddressUtil';
import { MapPin } from 'lucide-react';
import { formatAvailableDate } from '../util/DateFormatterUtil';
import { Bathroom, Listing, RoomType } from '../api/listings-client';
import LazyImage from '../components/LazyImage'; // Adjust the import path as needed

const generateListingUrl = (listing: Listing): string | null => {
    const {
        id,
        suburb,
        postcode,
        state,
        bathroom,
        furnished,
        parking,
        roomType
    } = listing;

    if (!suburb || !postcode || !state || !id) return null;
    
    const features = [];
    features.push(roomType === RoomType.OWN ? 'private' : 'shared');
    if (furnished) features.push('furnished');
    features.push('room');
    
    if (bathroom === Bathroom.OWN || parking) {
        features.push('with');
        if (bathroom === Bathroom.OWN) {
            features.push('ensuite');
            if (parking) features.push('and');
        }
        if (parking) features.push('parking');
    }
    
    const urlPath = `/share-house/${suburb.toLowerCase()}-${postcode}/${features.join('-')}/${id}`;
    return urlPath;
};

interface ListingCardProps {
    listing: Listing | null; // Listing null implies loading state
}

const ListingCard = React.forwardRef<HTMLDivElement, ListingCardProps>(
    ({ listing }, ref) => {
        const today = new Date().toISOString().split('T')[0];
        const availabilityText = listing && listing?.availableFrom 
            ? (new Date(listing.availableFrom) <= new Date(today) 
                ? 'Available now' 
                : `Available ${formatAvailableDate(listing.availableFrom)}`)
            : '';
        
        // Get the image URL if available, or empty string for loading state
        const imageUrl = listing && listing?.images?.at(0)?.small ? listing.images.at(0).small : '';

        return (
            <div
                ref={ref}
                className="group relative w-full flex flex-col rounded-xl bg-white border border-gray-200 h-full shadow-md transition-all duration-200 ease-in-out hover:shadow-lg select-none"
            >
                {/* Gradient border animation */}
                {listing && (
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#24ccf2]/0 via-[#2476f2]/0 to-purple-500/0 opacity-0 group-hover:opacity-100 group-hover:from-[#24ccf2]/10 group-hover:via-[#2476f2]/10 group-hover:to-purple-500/10 transition-opacity duration-200 ease-in-out -m-[1px]" />
                )}
    
                {/* The actual content structure - consistent for both loading and loaded states */}
                <div className="flex flex-col h-full text-left relative">
                    {/* Image Container - same structure in both states */}
                    <div className="relative pb-[70%] w-full overflow-hidden rounded-t-xl">
                        <div className="absolute inset-0">
                            <LazyImage
                                src={imageUrl}
                                alt={listing && listing?.suburb ? capitalizeFirstLetters(listing.suburb) : 'Loading'}
                                className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-200 ease-in-out group-hover:scale-105"
                                forceLoading={listing == null} // Keep the skeleton visible during loading
                            />
                        </div>
                    </div>

                    {/* Content area */}
                    <div className="p-4 flex flex-col gap-2">
                        {/* Price */}
                        {listing ? (
                            <div className="flex items-baseline gap-1">
                                <span className="text-lg font-medium text-gray-900">${listing?.rent} / week</span>
                            </div>
                        ) : (
                            <div className="flex items-baseline gap-1">
                                <div className="h-7 bg-gray-200 rounded w-24 animate-pulse"></div>
                            </div>
                        )}

                        {/* Location */}
                        {listing ? (
                            <div className="flex items-center gap-1.5 text-gray-600">
                                <MapPin className="w-4 h-4" />
                                <p className="text-sm">
                                    {capitalizeFirstLetters(listing?.suburb || '')}, {listing?.state} {listing?.postcode}
                                </p>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5">
                                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                                <div className="h-5 bg-gray-200 rounded w-36 animate-pulse"></div>
                            </div>
                        )}

                        {/* Availability */}
                        {listing ? (
                            <div className="inline-flex">
                                <span className={`text-sm px-2.5 py-1 rounded-full ${
                                    availabilityText === 'Available now'
                                        ? 'bg-[#24ccf2]/10 text-[#2476f2]'
                                        : 'bg-gray-100 text-gray-600'
                                }`}>
                                    {availabilityText}
                                </span>
                            </div>
                        ) : (
                            <div className="h-7 bg-gray-200 rounded-full w-28 animate-pulse"></div>
                        )}
                    </div>
                </div>
                
                {/* Link overlay for clickable area - only when loaded */}
                {listing && (
                    <Link
                        to={generateListingUrl(listing) ?? `/listing/${listing.id}`}
                        state={{ listing }}
                        className="absolute inset-0 z-10"
                        aria-label={`View details for ${listing.suburb} property`}
                    />
                )}
            </div>
        );
    }
);

export { ListingCard };